import React, {useEffect, useState} from 'react';
import {ErrorMessage, Field, Form, Formik} from 'formik';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {IoChevronDown, IoChevronUp} from 'react-icons/io5';
import "pages/grounds/_groundDetail.scss";
import "pages/grounds/_groundCreate.scss";
import {api} from 'api/calls';
import {routes} from 'common';
import {DAYS_OF_WEEK, groundValidationSchema, MAX_FILE_SIZE} from 'pages/grounds/constants';
import {useDispatch, useSelector} from 'react-redux';
import {ComLoader, Loader, MultiImages, MultiSelect, GoogleLocationPicker} from 'components';
import {GOOGLE_MAPS_API_KEY} from 'config';
import {generalExtraActions} from 'reduxStore/actions';

function GroundCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    sports,
    sportsLoading,
    sportsError,
    amenities,
    amenitiesLoading,
    amenitiesError
  } = useSelector((state) => state.general);
  const [loading, setLoading] = useState(false);
  const [expandedFields, setExpandedFields] = useState(new Set()); // All fields collapsed by default
  const [expandedSlots, setExpandedSlots] = useState(new Set());
  const [expandedDays, setExpandedDays] = useState(new Set()); // Track expanded days for slots
  const [expandedLocation, setExpandedLocation] = useState(true); // Track location section expansion
  const [expandedGroundFields, setExpandedGroundFields] = useState(false); // Track ground fields section expansion
  const [expandedBasicInfo, setExpandedBasicInfo] = useState(true); // Track basic info section expansion (expanded by default)
  const [expandedImages, setExpandedImages] = useState(true); // Track images section expansion
  const [uploadError, setUploadError] = useState(''); // Track upload errors
  const [dragOver, setDragOver] = useState(false); // Track drag and drop state

  useEffect(() => {
    dispatch(generalExtraActions.getSports());
    dispatch(generalExtraActions.getAmenities());
  }, [dispatch]);

  // Function to generate 1-hour slots between opening and closing time
  const generateHourlySlots = (openingTime, closingTime) => {
    if (!openingTime || !closingTime) return {};

    const slots = {};
    const opening = new Date(`2000-01-01T${openingTime}:00`);
    const closing = new Date(`2000-01-01T${closingTime}:00`);

    // Generate slots for each day
    DAYS_OF_WEEK.forEach(day => {
      slots[day] = [];
      let currentTime = new Date(opening);

      while (currentTime < closing) {
        const startTime = currentTime.toTimeString().slice(0, 5);
        currentTime.setHours(currentTime.getHours() + 1);
        const endTime = currentTime.toTimeString().slice(0, 5);

        // Only add slot if end time doesn't exceed closing time
        if (currentTime <= closing) {
          slots[day].push({
            start_time: startTime,
            end_time: endTime,
            price: 1000
          });
        }
      }
    });

    return slots;
  };

  // Function to update all field slots when opening/closing time changes

  const initialValues = {
    name: '',
    contact_number: '',
    email: '',
    description: '',
    opening_time: '',
    closing_time: '',
    is_active: true,
    // sports removed; will be inferred from ground fields
    amenities: [],
    location: {
      city: '',
      address: '',
      latitude: null,
      longitude: null,
    },
    images: [],
  };

  const handleSubmit = async (values, {setSubmitting, setFieldError}) => {
    setLoading(true);
    try {
      // Validate required fields before submission
      const requiredFields = {
        name: values.name,
        contact_number: values.contact_number,
        email: values.email,
        description: values.description,
        opening_time: values.opening_time,
        closing_time: values.closing_time,
        amenities: values.amenities,
        location: values.location,
      };

      // Check if any required fields are empty
      const emptyFields = Object.entries(requiredFields).filter(([key, value]) => {
        if (key === 'location') {
          return !value.city || !value.address;
        }
        if (Array.isArray(value)) {
          return !value || value.length === 0;
        }
        return !value || value === '';
      });

      if (emptyFields.length > 0) {
        toast.error('Please fill in all required fields before submitting.');
        setLoading(false);
        setSubmitting(false);
        return;
      }

      // Function to convert file to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };

      // Convert images to base64
      const convertImagesToBase64 = async (images) => {
        const convertedImages = [];
        for (const image of images) {
          if (image.file) {
            try {
              const base64 = await fileToBase64(image.file);
              convertedImages.push({
                image: base64,
                is_primary: image.is_primary
              });
            } catch (error) {
              console.error('Error converting image to base64:', error);
              toast.error(`Error processing image: ${image.file.name}`);
            }
          }
        }
        return convertedImages;
      };

      // Convert main ground images
      const convertedMainImages = await convertImagesToBase64(values.images);

      // Prepare JSON data
      const jsonData = {
        name: values.name,
        contact_number: values.contact_number,
        email: values.email,
        description: values.description,
        opening_time: values.opening_time,
        closing_time: values.closing_time,
        is_active: values.is_active,
        amenities: values.amenities,
        location: {
          city: values.location.city,
          address: values.location.address,
          latitude: values.location.latitude ? parseFloat(Number(values.location.latitude).toFixed(8)) : null,
          longitude: values.location.longitude ? parseFloat(Number(values.location.longitude).toFixed(8)) : null,
          place_id: values.location.place_id || null,
        },
        images: convertedMainImages
      };

      // Debug: Log what's being sent
      console.log('JSON data being sent:', jsonData);

      await api.createGround(jsonData);
      toast.success('Ground created successfully!');
      navigate(routes.GROUNDS);
    } catch (error) {
      console.error('Error creating ground:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(routes.GROUNDS);
  };

  const toggleFieldExpansion = (fieldIndex) => {
    const newExpandedFields = new Set(expandedFields);
    if (newExpandedFields.has(fieldIndex)) {
      newExpandedFields.delete(fieldIndex);
    } else {
      newExpandedFields.add(fieldIndex);
    }
    setExpandedFields(newExpandedFields);
  };

  const toggleSlotsExpansion = (fieldIndex) => {
    const newExpandedSlots = new Set(expandedSlots);
    if (newExpandedSlots.has(fieldIndex)) {
      newExpandedSlots.delete(fieldIndex);
    } else {
      newExpandedSlots.add(fieldIndex);
    }
    setExpandedSlots(newExpandedSlots);
  };

  const toggleDayExpansion = (day) => {
    const newExpandedDays = new Set(expandedDays);
    if (newExpandedDays.has(day)) {
      newExpandedDays.delete(day);
    } else {
      newExpandedDays.add(day);
    }
    setExpandedDays(newExpandedDays);
  };

  const toggleLocationExpansion = () => {
    setExpandedLocation(!expandedLocation);
  };

  const toggleGroundFieldsExpansion = () => {
    setExpandedGroundFields(!expandedGroundFields);
  };

  const toggleBasicInfoExpansion = () => {
    setExpandedBasicInfo(!expandedBasicInfo);
  };

  const toggleImagesExpansion = () => {
    setExpandedImages(!expandedImages);
  };

  // Image handling functions
  const validateImage = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 2MB';
    }
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      return 'Only JPEG, PNG and GIF files are allowed';
    }
    return null;
  };

  const handleImageUpload = (event, setFieldValue, values) => {
    const files = Array.from(event.target.files);
    setUploadError('');

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const error = validateImage(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setUploadError(errors.join(', '));
      return;
    }

    // Create new image objects
    const newImages = validFiles.map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      image: URL.createObjectURL(file),
      file: file, // Store the actual file for upload
      is_primary: values.images.length === 0 && index === 0 // First image becomes primary if no images exist
    }));

    setFieldValue('images', [...values.images, ...newImages]);
  };

  const handleSetPrimary = (imageIndex, setFieldValue, values) => {
    // Update local state for immediate UI feedback
    const updatedImages = values.images.map((image, index) => ({
      ...image,
      is_primary: index === imageIndex
    }));
    setFieldValue('images', updatedImages);

    toast.success('Primary image updated.');
  };

  const handleDeleteImage = (imageIndex, setFieldValue, values) => {
    const imageToDelete = values.images[imageIndex];

    const updatedImages = values.images.filter((_, index) => index !== imageIndex);

    // If we're deleting the primary image and there are other images, make the first one primary
    if (imageToDelete.is_primary && updatedImages.length > 0) {
      updatedImages[0].is_primary = true;
    }

    setFieldValue('images', updatedImages);
    toast.success('Image removed.');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e, setFieldValue, values) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const event = {target: {files}};
      handleImageUpload(event, setFieldValue, values);
    }
  };

  return (<div className="ground-detail-page detail-page">
    <div className="header">
      <h1>Create New Ground</h1>
      <p className="subtitle">Add a new ground with all its details and settings</p>
    </div>

    <Formik
      initialValues={initialValues}
      validationSchema={groundValidationSchema}
      onSubmit={handleSubmit}
      validateOnMount={false}
      validateOnChange={false}
      validateOnBlur={true}
      context={{isSubmitting: false}}
    >
      {({values, errors, touched, setFieldValue}) => {
        return <Form className="custom-form">
          {/* Basic Information Section */}
          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={toggleBasicInfoExpansion}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBasicInfoExpansion();
                }}
                aria-label={expandedBasicInfo ? 'Collapse basic information' : 'Expand basic information'}
              >
                  <span className={`expand-icon ${expandedBasicInfo ? 'expanded' : ''}`}>
                    {expandedBasicInfo ? <IoChevronUp/> : <IoChevronDown/>}
                  </span>
              </button>
              <h2>Basic Information</h2>
            </div>
          </div>

          {expandedBasicInfo && (<div className="basic-info-content">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">
                  Ground Name <span className="required">*</span>
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                  placeholder="Enter ground name"
                />
                <ErrorMessage name="name" component="div" className="error-message"/>
              </div>
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                  placeholder="Enter email address"
                />
                <ErrorMessage name="email" component="div" className="error-message"/>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Description <span className="required">*</span>
              </label>
              <Field
                as="textarea"
                id="description"
                name="description"
                className={`form-control ${touched.description && errors.description ? 'is-invalid' : ''}`}
                placeholder="Enter ground description"
                rows="4"
              />
              <ErrorMessage name="description" component="div" className="error-message"/>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact_number">
                  Contact Number <span className="required">*</span>
                </label>
                <Field
                  type="tel"
                  id="contact_number"
                  name="contact_number"
                  className={`form-control ${touched.contact_number && errors.contact_number ? 'is-invalid' : ''}`}
                  placeholder="Enter contact number"
                />
                <ErrorMessage name="contact_number" component="div" className="error-message"/>
              </div>
              <div className="form-group">
                <label htmlFor="is_active">
                  Status
                </label>
                <Field
                  as="select"
                  id="is_active"
                  name="is_active"
                  className={`form-control ${touched.is_active && errors.is_active ? 'is-invalid' : ''}`}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </Field>
                <ErrorMessage name="is_active" component="div" className="error-message"/>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="opening_time">
                  Opening Time <span className="required">*</span>
                </label>
                <Field
                  type="time"
                  id="opening_time"
                  name="opening_time"
                  className={`form-control ${touched.opening_time && errors.opening_time ? 'is-invalid' : ''}`}
                  onChange={(e) => {
                    setFieldValue('opening_time', e.target.value);
                    // Trigger validation
                    setTimeout(() => {
                      const form = e.target.closest('form');
                      if (form) {
                        const event = new Event('change', {bubbles: true});
                        form.dispatchEvent(event);
                      }
                    }, 0);
                  }}
                />
                <ErrorMessage name="opening_time" component="div" className="error-message"/>
              </div>
              <div className="form-group">
                <label htmlFor="closing_time">
                  Closing Time <span className="required">*</span>
                </label>
                <Field
                  type="time"
                  id="closing_time"
                  name="closing_time"
                  className={`form-control ${touched.closing_time && errors.closing_time ? 'is-invalid' : ''}`}
                  onChange={(e) => {
                    setFieldValue('closing_time', e.target.value);
                    // Trigger validation
                    setTimeout(() => {
                      const form = e.target.closest('form');
                      if (form) {
                        const event = new Event('change', {bubbles: true});
                        form.dispatchEvent(event);
                      }
                    }, 0);
                  }}
                />
                <ErrorMessage name="closing_time" component="div" className="error-message"/>
              </div>
            </div>

            {values.opening_time && values.closing_time && (
              (() => {
                const openingTime = new Date(`2000-01-01T${values.opening_time}:00`);
                const closingTime = new Date(`2000-01-01T${values.closing_time}:00`);
                if (openingTime >= closingTime) {
                  return <div className="error-message time-validation-error">
                    {'Opening time must be less than closing time'}
                  </div>

                }
                return null;
              })()
            )}

            {/* Sports removed; ground sports derive from ground field sports */}

            {/* Amenities Section */}
            <div className="form-group">
              <label>
                Amenities <span className="required">*</span>
              </label>
              {amenitiesLoading ? (
                <div style={{color: '#888', padding: '8px 0'}}>Loading amenities...</div>
              ) : amenitiesError ? (
                <div style={{color: '#d32f2f', padding: '8px 0'}}>Failed to load amenities</div>
              ) : (
                <MultiSelect
                  options={amenities.map(a => ({label: a.name, value: a.id}))}
                  value={amenities.filter(a => (values.amenities || []).includes(a.id)).map(a => ({
                    label: a.name,
                    value: a.id
                  }))}
                  onChange={selected => setFieldValue('amenities', selected.map(opt => opt.value))}
                  placeholder="Select amenities..."
                />
              )}
              <ErrorMessage name="amenities" component="div" className="error-message"/>
            </div>
          </div>)}

          {/* Images Section */}
          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={toggleImagesExpansion}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleImagesExpansion();
                }}
                aria-label={expandedImages ? 'Collapse images' : 'Expand images'}
              >
                  <span className={`expand-icon ${expandedImages ? 'expanded' : ''}`}>
                    {expandedImages ? <IoChevronUp/> : <IoChevronDown/>}
                  </span>
              </button>
              <h2>Ground Images</h2>
            </div>
          </div>

          {expandedImages && (<div className="images-content">
            <MultiImages
              images={values.images}
              onSetPrimary={index => handleSetPrimary(index, setFieldValue, values)}
              onDelete={index => handleDeleteImage(index, setFieldValue, values)}
              onUpload={e => handleImageUpload(e, setFieldValue, values)}
              loading={loading}
              errors={errors.images}
              title="Ground Images"
            />
            {uploadError && (<div className="upload-error">{uploadError}</div>)}
          </div>)}

          {/* Location Section */}
          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={toggleLocationExpansion}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLocationExpansion();
                }}
                aria-label={expandedLocation ? 'Collapse location' : 'Expand location'}
              >
                  <span className={`expand-icon ${expandedLocation ? 'expanded' : ''}`}>
                    {expandedLocation ? <IoChevronUp/> : <IoChevronDown/>}
                  </span>
              </button>
              <h2>Location Information</h2>
            </div>
          </div>

          {expandedLocation && (<div className="location-content">
            <div className="form-group">
              <label>
                Location <span className="required">*</span>
              </label>
              <GoogleLocationPicker
                value={values.location}
                onChange={(location) => {
                  setFieldValue('location', location);
                }}
                apiKey={GOOGLE_MAPS_API_KEY}
              />
              {values.location?.address && (
                <div className="location-display" style={{marginTop: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
                  <div style={{fontWeight: '500', marginBottom: '4px'}}>Selected Location:</div>
                  <div style={{color: '#666', fontSize: '14px'}}>
                    <div><strong>Address:</strong> {values.location.address}</div>
                    {values.location.city && (
                      <div style={{marginTop: '4px'}}><strong>City:</strong> {values.location.city}</div>
                    )}
                  </div>
                </div>
              )}
              <ErrorMessage name="location.address" component="div" className="error-message"/>
              <ErrorMessage name="location.city" component="div" className="error-message"/>
            </div>
            
            {/* Hidden fields for address and city to maintain form validation */}
            <Field
              type="hidden"
              name="location.address"
              value={values.location?.address || ''}
            />
            <Field
              type="hidden"
              name="location.city"
              value={values.location?.city || ''}
            />
          </div>)}

          {/* Actions Section */}
          <div className="actions-section">
            <button
              className="submit-btn primary"
              type="submit"
              disabled={loading}
            >
              {loading ? <ComLoader/> : "Create Ground"}
            </button>

            <button
              className="submit-btn secondary"
              type="button"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </Form>
      }}
    </Formik>
  </div>);
}

export default GroundCreate;